import { useState } from 'react';
import type { CSSProperties } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from 'react-router-dom';
import { ErrorBanner, Field, Loading, Modal, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { formatCompactCurrency, heatClass, maturityLabel } from '../lib/format';
import { useHeldResource, useResource } from '../lib/hooks';
import { pageKey } from '../lib/pages';
import type { MaSummary } from './MaPage';
import { projectHeadlines, type AtProject } from './ProjectsAutotaskDashboard';
import { useServiceHeadlines } from './ServiceDeliveryPage';
import type {
  AdminSummary,
  BusinessArea,
  ComplianceSummary,
  CsSummary,
  DashboardSummary,
  Department,
  EosSummary,
  Me,
  NocSummary,
  SalesSummary,
  ServiceDelivery,
  AmSummary,
  PeopleSummary,
} from '../lib/types';

/**
 * Functions this app runs itself, by area code.
 *
 * Most tiles link out to another app in the estate. These ones link inward to
 * a module of the COO Toolbox, so the destination is a route rather than a
 * URL someone has to configure. An explicitly set AppUrl still wins — if
 * somebody has typed a URL, they meant it.
 */
const INTERNAL_MODULES: Record<string, string> = {
  sales: '/sales',
  compliance: '/compliance',
  delivery: '/service-delivery',
  am: '/account-management',
  ma: '/ma',
  /* The CS card. Like `hr` below, the estate registered a CS Toolbox app under
     this key — and like the HR Toolbox, that app was moved in here rather than
     kept, so the tile links inward. */
  cs: '/cs',
  /* The Projects card. Like `cs` above, the estate registered a Professional
     Services portal under this key — and like that app, it was moved in here
     rather than kept, so the tile links inward. See migration 044. */
  projects: '/projects',
  /* The NOC card. Same story again: the estate registered a NOC dashboard app
     under this key, and it was moved in here rather than kept. See migration
     047. */
  noc: '/noc',
  /* The People card. `hr` is its area code because the estate registered an HR
     Toolbox app under that key — but that app was moved in here rather than
     deployed, so the tile links inward like the rest of these. */
  hr: '/people',
  /* The Admin card. Same story once more: the estate registered an operations
     admin dashboard under this key, and it was moved in here rather than kept.
     See migration 049. */
  admin: '/admin',
  /* The EOS card. Unlike every other key here, the estate never registered an
     app under it — the area is new in seed 001 and was created FOR this
     module, folded in from `C:\Development\EOS - Lite` by migration 058. So
     there is no AppUrl to have been cleared and no card-points-inward
     migration to write; it has only ever pointed here. */
  eos: '/eos',
};

/**
 * A tile links into its module only for somebody who may open that page
 * (migration 144). The card decides that the tile is on the dashboard; the
 * page list decides whether it is a link. Without the page it stays a dial
 * with its headline figures — the biggest circle on the dashboard leading to
 * "No access" is not a reasonable way to show somebody a number.
 */

/**
 * One figure on a tile.
 *
 * The FIRST one a tile supplies is its headline and goes on the dial; the rest
 * are captioned underneath. So the order a module lists these in is a
 * decision about what that area leads with, not an arrangement.
 */
interface TileMetric {
  value: string | number;
  label: string;
  /** Crimson, because this number is the reason the area wants attention. */
  flagged?: boolean;
  /** Greyed, for a placeholder dash rather than a figure. */
  muted?: boolean;
  /** A dependency risk score, drawn as a heat cell instead of a plain figure. */
  heat?: number;
}

/**
 * What a module-backed tile shows instead of the generic rollup.
 *
 * Headcount and open rocks are the right summary for an area this app only
 * knows about through its people. For an area this app actually runs, they
 * are the least interesting thing on the tile — so a module supplies its own
 * four figures, read from the same endpoint its page uses so the two cannot
 * drift apart.
 */
interface ModuleTile {
  metrics: TileMetric[];
  note?: string;
  attention: boolean;
}

/** Position of one area in a local arrangement; unmentioned ids sort last. */
function rank(order: number[], areaId: number): number {
  const i = order.indexOf(areaId);
  return i < 0 ? Number.MAX_SAFE_INTEGER : i;
}

/**
 * The hub. One tile per business function.
 *
 * Each tile rolls up COO Toolbox data for the people in that area, and links
 * out to the app that runs it once that app exists. Until then the tile is a
 * summary, not a dead link.
 */
export function DashboardPage() {
  const areas = useResource<BusinessArea[]>('/dashboard/areas');
  /* Behind requireAuth alone, unlike /eos/summary — which is why the maturity
     score is served from here. See the note on the query. */
  const summary = useResource<DashboardSummary>('/dashboard/summary');
  /* Only to decide whether a tile links anywhere — see the note above. */
  const me = useResource<Me>('/me');
  const canOpen = (path: string) =>
    me.data?.appRole === 'admin' || (me.data?.pages?.includes(pageKey(path)) ?? false);

  /** The path a tile opens, or undefined when this person cannot open it. */
  const linkFor = (code: string): string | undefined => {
    const path = INTERNAL_MODULES[code];
    return path && canOpen(path) ? path : undefined;
  };
  const departments = useResource<Department[]>('/departments');
  const sales = useResource<SalesSummary>('/sales/summary');
  const compliance = useResource<ComplianceSummary>('/compliance/summary');
  /* Targets only — the figures are the page's live dials, below. */
  const service = useResource<ServiceDelivery>('/service');
  /* The live dials ask Autotask, so only when this person has the tile. */
  const live = useServiceHeadlines((areas.data ?? []).some((a) => a.Code === 'delivery'));
  const am = useResource<AmSummary>('/am/summary');
  const people = useResource<PeopleSummary>('/people/summary');
  const cs = useResource<CsSummary>('/cs/summary');
  /* What the Projects page reads — Autotask — and held the same way, so
     opening the page after the hub (or the hub after the page) asks Autotask
     once. The old /projects/summary counted the local portal table the page
     stopped showing in #53. */
  const projects = useHeldResource<{ projects: AtProject[] }>('/projects/autotask');
  /* M&A is admin-only (server.ts mounts /api/ma behind requireAdmin), so only
     an admin asks; everyone else's tile stays the area rollup. */
  const ma = useResource<MaSummary>(me.data?.appRole === 'admin' ? '/ma/summary' : null);
  const noc = useResource<NocSummary>('/noc/summary');
  const admin = useResource<AdminSummary>('/admin/summary');
  const eos = useResource<EosSummary>('/eos/summary');
  const [editing, setEditing] = useState<BusinessArea | null>(null);

  /* The arrangement this browser is showing, as area ids. Null until the
     person drags something, which is what makes the server's order — theirs
     if they have one, the organisation's if not — the thing on screen.
     Held as IDS rather than as a reordered copy of the rows so it survives a
     reload of the underlying figures: `areas.reload()` after an edit replaces
     every row object, and an order expressed as ids still applies to them. */
  const [order, setOrder] = useState<number[] | null>(null);
  const [orderProblem, setOrderProblem] = useState<string | null>(null);

  const sensors = useSensors(
    /* A few pixels before a drag begins, so a click that lands on the handle
       and a drag that starts there stay distinguishable. */
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /* Null until the summary lands, and null when no assessment is complete —
     the dial treats both the same way, by not offering the slot. */
  const omlScore = summary.data?.OmlScore ?? null;

  /**
   * Operational maturity, from the last COMPLETED assessment.
   *
   * Its own list so it can be appended to the EOS figures OR stand alone as
   * the whole tile. That second case is the one that matters: everybody holds
   * the EOS card now (migration 081) but /eos/summary is still leadership-only,
   * so for most people `eos.data` is null and the four weekly figures never
   * arrive. Building this inside that branch — which is where it started —
   * meant the score was served to everyone and rendered for nobody outside the
   * leadership team, which is the exact opposite of the point.
   *
   * Level 3 is where a business stops firefighting; below it is flagged, the
   * same call the OML page's own banding makes.
   */
  const omlMetrics: TileMetric[] =
    omlScore === null
      ? []
      : [
          {
            value: omlScore.toFixed(2),
            label: maturityLabel(omlScore).toLowerCase(),
            flagged: omlScore < 3,
          },
        ];

  const moduleTile = (code: string): ModuleTile | null => {
    if (code === 'sales' && sales.data) {
      const s = sales.data;
      return {
        metrics: [
          { value: formatCompactCurrency(s.OpenPipelineValue), label: 'open pipeline' },
          {
            value: s.OpenDeals,
            label: s.OverdueCloseDates > 0 ? `deals · ${s.OverdueCloseDates} late` : 'open deals',
            flagged: s.OverdueCloseDates > 0,
          },
          { value: formatCompactCurrency(s.WeightedForecast), label: 'weighted' },
          { value: formatCompactCurrency(s.WonMrrThisQuarter), label: 'MRR won this qtr' },
        ],
        note:
          s.DealsWithoutNextStep > 0
            ? `${s.DealsWithoutNextStep} ${s.DealsWithoutNextStep === 1 ? 'deal has' : 'deals have'} no next step`
            : undefined,
        attention: s.OverdueCloseDates > 0 || s.OverdueActivities > 0,
      };
    }

    if (code === 'cs' && cs.data) {
      const c = cs.data;
      return {
        metrics: [
          { value: c.audits.withOpenItems, label: 'audits open', flagged: c.audits.withOpenItems > 0 },
          { value: c.audits.clientAudits, label: 'client audits' },
          { value: c.tools.total, label: 'tools' },
          { value: c.vendors.total, label: 'vendors' },
        ],
        /* Undocumented tools rather than documented ones: the number that says
           what is left to do is the one worth putting on the hub. */
        note:
          c.tools.total > c.tools.withPlaybook
            ? `${c.tools.total - c.tools.withPlaybook} of ${c.tools.total} tools have no playbook`
            : undefined,
        attention: c.audits.withOpenItems > 0,
      };
    }

    if (code === 'projects' && projects.data) {
      /* The Projects page's own headline figures, over the same projects with
         its default view (every project, all time). */
      const p = projectHeadlines(projects.data.projects);
      const pct = p.est > 0 ? Math.round((p.used / p.est) * 100) : null;
      return {
        metrics: [
          { value: p.open, label: 'open projects' },
          { value: p.overdue, label: 'past end date', flagged: p.overdue > 0 },
          { value: p.overHours, label: 'over hours', flagged: p.overHours > 0 },
          {
            value: pct === null ? '—' : `${pct}%`,
            label: 'hours used',
            /* Over budget on the open work is unbilled effort. */
            flagged: pct !== null && pct > 100,
          },
        ],
        note: p.overdueTasks > 0 ? `${p.overdueTasks} with a late task` : undefined,
        attention: p.overdue > 0 || p.overHours > 0,
      };
    }

    if (code === 'ma' && ma.data) {
      const m = ma.data;
      /* The M&A page's own module cards, done against total. */
      return {
        metrics: [
          { value: `${m.DdComplete}/${m.DdTotal}`, label: m.DdInProgress > 0 ? `DD requests · ${m.DdInProgress} in progress` : 'DD requests done' },
          { value: `${m.DataRoomReceived}/${m.DataRoomTotal}`, label: 'data room received' },
          { value: `${m.ToolboxDone}/${m.ToolboxTotal}`, label: 'toolbox done' },
          { value: m.PlanSteps, label: m.Plans === 1 ? 'integration steps' : `steps · ${m.Plans} plans` },
        ],
        note: m.GwcPeople > 0 ? `${m.GwcRightSeat} of ${m.GwcPeople} right person, right seat` : undefined,
        attention: false,
      };
    }

    if (code === 'noc' && noc.data) {
      const n = noc.data;
      return {
        metrics: [
          { value: n.alerts, label: 'alerts in the library' },
          { value: n.categories, label: 'technology categories' },
          { value: n.critical, label: 'rated critical' },
          { value: n.withRemediation, label: 'with remediation' },
        ],
        /* The gap, not the total: how many records tell you what to DO about the
           alert is the number that says whether the library is worth opening. */
        note:
          n.withRemediation < n.alerts
            ? `${n.alerts - n.withRemediation} have no recorded action`
            : undefined,
        /* Reference material has no overdue state — nothing here needs chasing. */
        attention: false,
      };
    }

    /* Not `&& eos.data`. Somebody without leadership gets a 403 from
       /eos/summary and so has no `e` — but they still hold the card, still see
       this tile, and are still meant to see the maturity score on it. */
    if (code === 'eos') {
      const e = eos.data;
      if (e === null) {
        /* The maturity score alone, or nothing — in which case this returns
           null and the tile falls back to the generic area figures, exactly as
           any unbacked tile does. */
        return omlMetrics.length === 0
          ? null
          : {
              metrics: omlMetrics,
              note: 'Operational maturity, from the last completed assessment',
              attention: false,
            };
      }
      /* No team at all is the state a database nobody has opened this module in
         is in, and it has to read as one — four zeroes here would report a
         leadership team that has stopped meeting rather than a module that is
         new. Same treatment as the Admin tile's empty registers. */
      if (e.teamId === null) {
        return {
          metrics: [...omlMetrics, { value: '—', label: 'no team set up yet' }],
          note: 'Level 10 meetings, the scorecard and this quarter’s rocks start with a team',
          attention: false,
        };
      }

      return {
        metrics: [
          {
            value: `${e.rocks.onTrack + e.rocks.complete}/${e.rocks.total}`,
            label: e.rocks.offTrack > 0 ? `rocks · ${e.rocks.offTrack} off track` : 'rocks on track',
            flagged: e.rocks.offTrack > 0,
          },
          {
            value: `${e.scorecard.hit}/${e.scorecard.scored}`,
            label: 'measurables on goal',
            flagged: e.scorecard.scored > 0 && e.scorecard.hit < e.scorecard.scored,
          },
          {
            value: e.todos.open,
            label: e.todos.overdue > 0 ? `to-dos · ${e.todos.overdue} overdue` : 'open to-dos',
            flagged: e.todos.overdue > 0,
          },
          {
            value: e.lastMeeting?.averageRating === null || e.lastMeeting === null
              ? '—'
              : e.lastMeeting.averageRating.toFixed(1),
            label: 'last meeting rated',
            /* An L10 is meant to score 8 or better; below that the cadence
               itself needs a conversation. */
            flagged: e.lastMeeting?.averageRating !== null && (e.lastMeeting?.averageRating ?? 10) < 8,
          },
          /* Empty when no assessment is complete, which leaves it off the
             rotation rather than showing a dash: the four above are weekly
             numbers and this one moves once a quarter, so an empty slot would
             read as something broken rather than something not yet done. */
          ...omlMetrics,
        ],
        /* The next meeting, or the absence of one. A leadership team with no
           L10 in the diary is the one thing on this tile worth chasing, and it
           cannot be read off any of the four figures above. */
        note:
          e.nextMeeting === null
            ? 'No Level 10 scheduled'
            : e.issues.openShort > 0
              ? `${e.issues.openShort} ${e.issues.openShort === 1 ? 'issue' : 'issues'} for the next IDS`
              : undefined,
        attention: e.rocks.offTrack > 0 || e.todos.overdue > 0 || e.nextMeeting === null,
      };
    }

    if (code === 'admin' && admin.data) {
      const a = admin.data;
      /* Nothing recorded anywhere is a real state for this module and has to read
         as one. Its registers ship empty by design — the standalone app's whole
         dataset was generated demo data and did not come across — so four zeroes
         here would report a back office in crisis rather than one nobody has
         started filling in. See migration 048. */
      /* Every register the Admin overview counts as "something recorded",
         not just four of them — POs or depot stock alone are a started module. */
      const started =
        a.contracts.total + a.registrations.total + a.sops.total + a.vendors.total +
          a.procurement.openPos + a.depot.stockUnits + a.depot.rmaOpen + a.scorecard.measures > 0 ||
        a.hasCallData;
      if (!started) {
        return {
          metrics: [{ value: '—', label: 'registers are empty' }],
          note: 'Contracts, SOPs, vendors and the paperwork register are waiting for a first entry',
          attention: false,
        };
      }

      return {
        metrics: [
          { value: formatCompactCurrency(a.contracts.clientMrr), label: 'client MRR' },
          {
            value: a.contracts.noticeDeadline30,
            label: a.contracts.noticeMissed > 0 ? `notice ≤30d · ${a.contracts.noticeMissed} missed` : 'notice deadlines ≤30d',
            flagged: a.contracts.noticeDeadline30 > 0 || a.contracts.noticeMissed > 0,
          },
          {
            value: a.registrations.lapsed,
            label: 'lapsed registrations',
            flagged: a.registrations.lapsed > 0,
          },
          {
            value: a.sops.overdue,
            label: 'SOPs overdue',
            flagged: a.sops.overdue > 0,
          },
        ],
        /* The one thing on this card that costs money if ignored, named first;
           otherwise whichever register is next most behind. */
        note:
          a.procurement.overdueReceipts > 0
            ? `${a.procurement.overdueReceipts} purchase ${a.procurement.overdueReceipts === 1 ? 'order is' : 'orders are'} past the promised date`
            : a.vendors.reviewsOverdue > 0
              ? `${a.vendors.reviewsOverdue} vendor ${a.vendors.reviewsOverdue === 1 ? 'review is' : 'reviews are'} overdue`
              : undefined,
        attention:
          a.registrations.lapsed > 0 ||
          a.contracts.noticeMissed > 0 ||
          a.contracts.noticeDeadline30 > 0 ||
          a.procurement.overdueReceipts > 0 ||
          a.sops.overdue > 0,
      };
    }

    if (code === 'compliance' && compliance.data) {
      const c = compliance.data;
      return {
        metrics: [
          { value: c.InScope, label: 'in scope' },
          { value: c.Maintained, label: 'maintained' },
          { value: c.Unowned, label: 'unowned', flagged: c.Unowned > 0 },
          {
            value: c.ReviewsOverdue,
            label: 'reviews overdue',
            flagged: c.ReviewsOverdue > 0,
          },
        ],
        /* No note. Whether a framework has been ruled in or out is a decision
           made per client on the assessment page, so a single count of the
           undecided is not a fact about the business the hub can state. */
        attention: c.Unowned > 0 || c.ReviewsOverdue > 0,
      };
    }

    if (code === 'delivery' && live.ready) {
      /* The page's live dials as it opens — CSAT today, billable this week,
         the SLA clocks this month, kill ratio today — not a recorded month. */
      const targets = service.data?.targets ?? null;
      const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${Math.round(v)}%`);
      const below = (v: number | null | undefined, target: number | undefined) =>
        v !== null && v !== undefined && target !== undefined && v < target;
      const sla = live.sla;
      /* Each clock against its own target, as the page does. */
      const missingSla =
        below(sla?.acknowledge.percent, targets?.SlaAcknowledgeTarget) ||
        below(sla?.response.percent, targets?.SlaResponseTarget) ||
        below(sla?.resolution.percent, targets?.SlaResolutionTarget);
      const killTarget = targets?.KillRatioTarget ?? 1;
      const lowKill = live.killRatio !== null && live.killRatio < killTarget;
      /* The CSAT target is a percentage; the survey is out of 5 (90% is 4.50). */
      const csatTarget = targets ? (targets.CsatTarget / 100) * 5 : undefined;
      return {
        metrics: [
          { value: live.csat === null ? '—' : live.csat.toFixed(2), label: 'CSAT today', flagged: below(live.csat, csatTarget) },
          { value: pct(live.billablePct), label: 'billable this week', flagged: below(live.billablePct, targets?.BillableTarget) },
          { value: pct(sla?.resolution.percent), label: 'SLA resolution · month', flagged: missingSla },
          { value: live.killRatio === null ? '—' : live.killRatio.toFixed(2), label: 'kill ratio today', flagged: lowKill },
        ],
        note: 'Live from Autotask',
        attention: missingSla || lowKill,
      };
    }

    if (code === 'am' && am.data) {
      const a = am.data;
      return {
        /* Six rather than four: the grid is auto-fit and wraps, and the two
           pairs below are the ones asked for on the hub. Reviews overdue and
           high risks move to the note — they are still flagged, and a count of
           nothing is a poor use of a tile slot. */
        metrics: [
          { value: a.Clients, label: 'clients' },
          { value: formatCompactCurrency(a.TotalMrr), label: 'MRR' },
          { value: a.AtRiskClients, label: 'at risk', flagged: a.AtRiskClients > 0 },
          {
            value: formatCompactCurrency(a.AtRiskMrr),
            label: 'MRR at risk',
            flagged: a.AtRiskMrr > 0,
            /* Nothing at risk reads better greyed than as a bold zero. */
            muted: a.AtRiskMrr === 0,
          },
          { value: a.OpenRecommendations, label: 'recommendations' },
          {
            /* Annualised so one number can carry both kinds. The split is in
               the note, because "£45k a year" and "£3k a month plus £9k once"
               answer different questions and the tile has room for one. */
            value: formatCompactCurrency(
              a.OpenRecommendationMonthly * 12 + a.OpenRecommendationAnnual + a.OpenRecommendationOneOff,
            ),
            label: 'if all approved',
            muted: a.OpenRecommendations === 0,
          },
        ],
        note: [
          a.ReviewsOverdue > 0 ? `${a.ReviewsOverdue} review${a.ReviewsOverdue === 1 ? '' : 's'} overdue` : null,
          a.HighRisks > 0 ? `${a.HighRisks} high or critical audit risks` : null,
          a.ClientsWithoutReview > 0 ? `${a.ClientsWithoutReview} with no review booked` : null,
          a.OpenRecommendations > 0
            ? `${formatCompactCurrency(a.OpenRecommendationMonthly)}/mo${a.OpenRecommendationAnnual > 0 ? ` + ${formatCompactCurrency(a.OpenRecommendationAnnual)}/yr` : ''} + ${formatCompactCurrency(a.OpenRecommendationOneOff)} one-off proposed`
            : null,
        ]
          .filter(Boolean)
          .join(' · ') || undefined,
        attention: a.ReviewsOverdue > 0 || a.HighRisks > 0 || a.AtRiskClients > 0,
      };
    }

    if (code === 'hr' && people.data) {
      const p = people.data;
      /* Training as a fraction, not a percentage: "142/180" says how much work
         is left and "79%" does not — and on a tile there is no room for both.
         Zero required training is not 100% complete, it is nothing set up, so
         it shows a dash rather than a flattering figure. */
      const training =
        p.RequiredTraining === 0 ? '—' : `${p.RequiredTrainingDone}/${p.RequiredTraining}`;

      return {
        metrics: [
          { value: p.People, label: 'people' },
          { value: p.ReviewsDue, label: 'reviews due', flagged: p.ReviewsDue > 0 },
          { value: p.ReviewsOpen, label: 'in draft' },
          {
            value: training,
            label: 'required training',
            flagged: p.RequiredTraining > 0 && p.RequiredTrainingDone < p.RequiredTraining,
          },
        ],
        note:
          p.Templates === 0
            ? 'No People Templates yet — reviews and training hang off them'
            : undefined,
        /* Reviews due is the figure that decays on its own: nobody has to do
           anything for it to get worse, which is exactly what the flag is for. */
        attention: p.ReviewsDue > 0,
      };
    }

    return null;
  };

  if (areas.loading) return <Loading label="Loading the dashboard" />;
  if (areas.error) return <ErrorBanner message={areas.error} />;

  const rows = areas.data ?? [];
  /* An id the local arrangement does not mention sorts last, which is the same
     rule the server applies — so a newly seeded area appears at the end here
     too rather than jumping about between the optimistic order and the saved
     one. Array.prototype.sort is stable, so those tail entries keep the order
     the server sent them in. */
  const tiles = order
    ? [...rows].sort((a, b) => rank(order, a.AreaId) - rank(order, b.AreaId))
    : rows;

  const onDragEnd = async (e: DragEndEvent) => {
    const activeId = Number(e.active.id);
    const overId = e.over ? Number(e.over.id) : null;
    if (!overId || activeId === overId) return;

    const ids = tiles.map((t) => t.AreaId);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;

    const next = arrayMove(ids, from, to);
    setOrder(next);
    setOrderProblem(null);
    try {
      await api.put('/dashboard/areas/order', { areaIds: next });
    } catch (err) {
      /* Put it back. A tile that stays where it was dropped but is somewhere
         else on the next reload is worse than one that visibly refuses. */
      setOrder(ids);
      setOrderProblem(err instanceof Error ? err.message : 'Could not save the new order');
    }
  };

  /* Split before rendering so the drag context never sees the pinned tile —
     dnd-kit reorders what it is given, and a sortable item rendered outside
     its context is one it cannot resolve. */
  const featured = tiles.find((t) => t.Code === 'eos') ?? null;
  const rest = tiles.filter((t) => t.Code !== 'eos');

  /* A function with nothing recorded gets a name in a quiet line at the
     bottom, not a card. Three of twelve were rendering as a bordered box
     containing the words "Nothing recorded yet", which is a lot of furniture
     to say nothing — and every dashboard guide says the same thing: a summary
     screen holds six to eight things, and anything that is not one of them
     belongs a click away rather than competing at the same size. */
  const hasFigures = (area: BusinessArea) => {
    const mod = moduleTile(area.Code);
    if (mod) return !mod.metrics.every((m) => m.muted || m.value === 0 || m.value === '0' || m.value === '—');
    return area.DepartmentCount > 0 && (area.Headcount > 0 || area.OpenRocks > 0 || area.OpenIssues > 0);
  };
  const active = rest.filter(hasFigures);
  const quiet = rest.filter((t) => !hasFigures(t));


  return (
    <div className="dash">
      <PageHead title="Dashboard" />

      {orderProblem && <ErrorBanner message={orderProblem} />}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => void onDragEnd(e)}
      >
        {/* rectSortingStrategy rather than the list one: these wrap into a
            grid, and the vertical strategy assumes a single column and slides
            the wrong neighbours out of the way. */}
        {/* EOS sits above the rest, on its own line, at twice the size. It is
            the operating cadence the other twelve functions are run BY, so it
            reads as the headline rather than as the thirteenth tile.

            PINNED, WHICH MEANS NOT DRAGGABLE. It is outside the SortableContext
            below, so its position is the layout's rather than the user's — the
            cost of the pin, and the reason it is not simply first in the grid
            with a wider span. Its SortOrder is left alone, so unpinning it is
            deleting this block. */}
        {featured && (
          <div className="area-featured">
            <AreaTile
              area={featured}
              index={0}
              internalPath={linkFor(featured.Code)}
              module={moduleTile(featured.Code)}
              onEdit={() => setEditing(featured)}
              pinned
            />
          </div>
        )}

        <SortableContext items={active.map((t) => t.AreaId)} strategy={rectSortingStrategy}>
          <div className="area-grid">
            {active.map((area, i) => (
              <AreaTile
                key={area.AreaId}
                area={area}
                index={i}
                  internalPath={linkFor(area.Code)}
                module={moduleTile(area.Code)}
                onEdit={() => setEditing(area)}
              />
            ))}
          </div>
        </SortableContext>

        {quiet.length > 0 && (
          <p className="fn-quiet">
            <span className="fn-quiet-label">Nothing recorded yet</span>
            {quiet.map((area) => {
              const to = INTERNAL_MODULES[area.Code];
              return to ? (
                <Link key={area.AreaId} to={to}>
                  {area.Name}
                </Link>
              ) : (
                <span key={area.AreaId}>{area.Name}</span>
              );
            })}
          </p>
        )}
      </DndContext>

      {editing && (
        <AreaEditor
          area={editing}
          departments={departments.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            areas.reload();
          }}
        />
      )}
    </div>
  );
}

function AreaTile({
  area,
  index,
  internalPath,
  module: mod,
  onEdit,
  pinned = false,
}: {
  area: BusinessArea;
  /** Position in the grid — staggers the entrance transition. */
  index: number;
  /** Set when this app runs the function itself — see `INTERNAL_MODULES`. */
  internalPath?: string;
  /** Set once a module backing this area has supplied its own figures. */
  module?: ModuleTile | null;
  onEdit: () => void;
  /** Rendered outside the sortable grid, at double size. Suppresses the drag
   *  grip, because there is nothing for this one to be dragged into. */
  pinned?: boolean;
}) {
  /* The whole tile moves, but only the grip starts a drag —
     `setActivatorNodeRef` is what separates the two. The alternative, putting
     the listeners on the tile itself, fights the link inside it: the browser
     natively drags an <a>, and a person aiming to open Sales would keep
     picking it up instead. */
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: area.AreaId });

  /* An area with no departments mapped has nothing to roll up, so its zeros
     are meaningless. Say "not mapped" instead of showing a row of noughts
     that reads as a problem. A tile backed by its own module has real figures
     either way, so it never shows that message. */
  const unmapped = area.DepartmentCount === 0 && !mod;

  const destination = area.AppUrl ?? internalPath ?? null;

  const attention =
    area.OffTrackRocks > 0 ||
    area.GwcConcerns > 0 ||
    area.CriticalDependencies > 0 ||
    (mod?.attention ?? false);

  /* One list either way, so the dial is built once rather than once per
     branch. The generic rollup used to be written out as its own block of
     markup; as figures it is the same shape as anything a module supplies,
     and the only thing it needs that they do not is the heat cell for a
     dependency risk score. */
  const metrics: TileMetric[] = mod
    ? mod.metrics
    : unmapped
      ? [{ value: '—', label: 'not mapped', muted: true }]
      : [
          { value: area.Headcount, label: area.Headcount === 1 ? 'person' : 'people' },
          {
            value: area.OpenRocks,
            label: area.OffTrackRocks > 0 ? `rocks · ${area.OffTrackRocks} off` : 'open rocks',
            flagged: area.OffTrackRocks > 0,
          },
          { value: area.OpenIssues, label: 'issues', flagged: area.OpenIssues > 0 },
          area.TopDependencyRisk === null
            ? { value: '—', label: 'no risk data', muted: true }
            : { value: area.TopDependencyRisk, label: 'top risk', heat: area.TopDependencyRisk },
        ];

  /* Nothing is recorded against this one yet. Rendered short and quiet rather
     than as four noughts: an empty function shouting zeros at the same volume
     as a real number is most of what made thirteen equal tiles unreadable. */
  const empty = metrics.every((m) => m.muted || m.value === 0 || m.value === '0' || m.value === '—');

  /* The description and the module's note, as the tooltip. The note's SIGNAL
     is on the face — the figure it is about turns crimson, and the card is
     already flagged. */
  const tooltip = [
    area.Description,
    mod
      ? mod.note
      : !unmapped && area.GwcConcerns > 0
        ? `${area.GwcConcerns} GWC ${area.GwcConcerns === 1 ? 'concern' : 'concerns'}`
        : undefined,
    unmapped ? 'No departments mapped yet' : undefined,
    destination
      ? undefined
      : area.AppRegistered
        ? 'App registered, no URL set'
        : 'No app yet',
  ]
    .filter(Boolean)
    .join('\n');

  /* The pinned tile is a BAND, not a bigger card.

     EOS is the cadence the other twelve functions are run by, and the account
     management hero band is the shape this app already uses to say "this is
     the header figure set for what you are looking at" — solid navy, cells
     divided by hairlines, every figure across one row. Reusing that shape
     makes the top of the hub read as a summary rather than as the first and
     largest of thirteen equal things. */
  const band = (
    <div className="fn-band">
      <div className="fn-band-head">
        <h3>{area.Name}</h3>
        {destination && (
          <span className="fn-go" aria-hidden="true">
            →
          </span>
        )}
      </div>
      <dl className="fn-band-figures">
        {metrics.map((m) => (
          <div key={m.label}>
            <dt className={m.flagged ? 'flagged' : m.muted ? 'subtle' : undefined}>{m.value}</dt>
            <dd>{m.label}</dd>
          </div>
        ))}
      </dl>
    </div>
  );

  const body = pinned ? band : (
    <div className={`fn-card${empty ? ' is-empty' : ''}`}>
      {/* The name sits on a solid ground across the top, the same treatment
          the pinned EOS band uses — so a card reads as a titled thing rather
          than as a heading floating above some numbers. */}
      <div className="fn-card-head">
        <h3 className="fn-name">{area.Name}</h3>
        {attention && <span className="dot area-flag" aria-label="Needs attention" />}
        {destination && (
          <span className="fn-go" aria-hidden="true">
            →
          </span>
        )}
      </div>

      {/* Every figure at once. The rotation this replaces showed one at a time,
          which meant no two functions could ever be compared — the single
          biggest thing wrong with the page it came from. */}
      <div className="fn-card-body">
      {empty ? (
        <p className="fn-empty">
          {unmapped ? 'No departments mapped yet' : 'Nothing recorded yet'}
        </p>
      ) : (
        <dl className="fn-figures">
          {metrics.map((m) => (
            <div key={m.label}>
              <dt>
                {m.heat === undefined ? (
                  <span
                    className={`fn-figure${m.flagged ? ' flagged' : ''}${m.muted ? ' subtle' : ''}`}
                  >
                    {m.value}
                  </span>
                ) : (
                  <span className={`heat ${heatClass(m.heat)}`}>{m.value}</span>
                )}
              </dt>
              <dd>{m.label}</dd>
            </div>
          ))}
        </dl>
      )}
      </div>
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      className={`area-tile${attention ? ' attention' : ''}${isDragging ? ' dragging' : ''}${pinned ? ' is-pinned' : ''}`}
      style={
        {
          '--tile-i': index,
          transform: CSS.Transform.toString(transform),
          transition,
        } as CSSProperties
      }
    >
      {/* An external app opens in a new tab; a module of this app is an
          ordinary route, so it navigates in place. Without either, the tile is
          a plain dial rather than a link that goes nowhere. */}
      {area.AppUrl ? (
        <a
          className="area-link"
          href={area.AppUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={tooltip || undefined}
        >
          <span className="visually-hidden">Open </span>
          {body}
        </a>
      ) : internalPath ? (
        <Link className="area-link" to={internalPath} title={tooltip || undefined}>
          <span className="visually-hidden">Open </span>
          {body}
        </Link>
      ) : (
        <div className="area-link" title={tooltip || undefined}>
          {body}
        </div>
      )}

      {/* Mirrors the edit button in the opposite corner, and lives in the
          same empty space a circle leaves in a square grid cell. dnd-kit's
          `attributes` carry the keyboard affordance, so this is draggable with
          the arrow keys as well as the pointer. */}
      {/* No grip on the pinned tile: its position is fixed by the layout, and
          a handle that picks up but has nowhere to go is worse than none. */}
      {!pinned && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          className="area-drag"
          aria-label={`Reorder ${area.Name}`}
          {...attributes}
          {...listeners}
        >
          ⠿
        </button>
      )}

      <button
        type="button"
        className="area-edit"
        onClick={(e) => {
          e.preventDefault();
          onEdit();
        }}
        aria-label={`Configure ${area.Name}`}
      >
        edit
      </button>
    </div>
  );
}

function AreaEditor({
  area,
  departments,
  onClose,
  onSaved,
}: {
  area: BusinessArea;
  departments: Department[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const mapped = useResource<{ DepartmentId: number; Name: string }[]>(
    `/dashboard/areas/${area.AreaId}/departments`,
  );

  const [name, setName] = useState(area.Name);
  const [appUrl, setAppUrl] = useState(area.AppUrl ?? '');
  const [selected, setSelected] = useState<number[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* Wait for the mapping to load before treating an empty selection as
     "deselect everything" — otherwise opening and saving immediately would
     silently clear the links. */
  const current = selected ?? mapped.data?.map((d) => d.DepartmentId) ?? null;

  const toggle = (id: number) => {
    const base = current ?? [];
    setSelected(base.includes(id) ? base.filter((x) => x !== id) : [...base, id]);
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/dashboard/areas/${area.AreaId}`, {
        name: name.trim(),
        appUrl: appUrl.trim() || null,
        ...(current !== null && { departmentIds: current }),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Configure ${area.Name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !name.trim() || mapped.loading}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Tile name">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <Field
        label="App URL"
        hint={
          area.AppKey
            ? `Registered in the estate as "${area.AppKey}". Leave blank until that app is deployed.`
            : 'Leave blank until this area has an app.'
        }
      >
        <input
          className="input"
          value={appUrl}
          onChange={(e) => setAppUrl(e.target.value)}
          placeholder="https://…"
        />
      </Field>

      <div className="field">
        <label>Departments in this area</label>
        <p className="xs muted">
          Rocks, issues and GWC records are attributed to an area through the people in its
          departments — so a tile with nothing mapped has nothing to report.
        </p>
        {mapped.loading ? (
          <Loading label="Loading mapping" />
        ) : (
          <div className="mt-2">
            {departments.length === 0 && <p className="muted small">No departments defined yet.</p>}
            {departments.map((d) => (
              <label key={d.DepartmentId} className="checkbox" style={{ display: 'flex', marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={(current ?? []).includes(d.DepartmentId)}
                  onChange={() => toggle(d.DepartmentId)}
                />
                {d.Name}
                <span className="xs subtle">
                  ({d.EmployeeCount ?? 0} {d.EmployeeCount === 1 ? 'person' : 'people'})
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      <p className="xs subtle">
        Tile names and descriptions are re-seeded on deploy, but the app URL and department mapping
        you set here are preserved.
      </p>

      <Link to="/employees" className="xs" onClick={onClose}>
        Manage departments →
      </Link>
    </Modal>
  );
}
